import type { StateCreator } from 'zustand';
import { CondoStore } from './shared';
import { apiService } from '../../services/api';

export const createBoardSlice: StateCreator<CondoStore, [], [], Partial<CondoStore>> = (set, get) => ({
  // ── Board Management ─────────────────────────────────────────────────────

  fetchBoardMembers: async (condoId?: string) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const boardMembers = await apiService.getBoardMembers(condoId);
      set({ boardMembers, error: null });
    } catch (error: any) {
      setError(error.message || 'Failed to fetch board members');
    } finally {
      setLoading(false);
    }
  },

  createBoardMember: async (memberData) => {
    const { setLoading, setError, fetchBoardMembers } = get();
    setLoading(true);
    try {
      await apiService.createBoardMember(memberData);
      await fetchBoardMembers(memberData.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to create board member');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  updateBoardMember: async (id, memberData) => {
    const { setLoading, setError, boardMembers, fetchBoardMembers } = get();
    setLoading(true);
    try {
      await apiService.updateBoardMember(id, memberData);
      const existing = boardMembers.find(m => m.id === id);
      await fetchBoardMembers(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to update board member');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  deleteBoardMember: async (id) => {
    const { setLoading, setError, boardMembers, fetchBoardMembers } = get();
    setLoading(true);
    try {
      const existing = boardMembers.find(m => m.id === id);
      await apiService.deleteBoardMember(id);
      await fetchBoardMembers(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to delete board member');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  fetchBoardMeetings: async (condoId?: string) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const boardMeetings = await apiService.getBoardMeetings(condoId);
      set({ boardMeetings, error: null });
    } catch (error: any) {
      setError(error.message || 'Failed to fetch board meetings');
    } finally {
      setLoading(false);
    }
  },

  createBoardMeeting: async (meetingData) => {
    const { setLoading, setError, fetchBoardMeetings } = get();
    setLoading(true);
    try {
      await apiService.createBoardMeeting(meetingData);
      await fetchBoardMeetings(meetingData.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to create board meeting');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  updateBoardMeeting: async (id, meetingData) => {
    const { setLoading, setError, boardMeetings, fetchBoardMeetings } = get();
    setLoading(true);
    try {
      await apiService.updateBoardMeeting(id, meetingData);
      const existing = boardMeetings.find(m => m.id === id);
      await fetchBoardMeetings(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to update board meeting');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  deleteBoardMeeting: async (id) => {
    const { setLoading, setError, boardMeetings, fetchBoardMeetings } = get();
    setLoading(true);
    try {
      const existing = boardMeetings.find(m => m.id === id);
      await apiService.deleteBoardMeeting(id);
      await fetchBoardMeetings(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to delete board meeting');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  fetchBoardResolutions: async (condoId?: string) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const boardResolutions = await apiService.getBoardResolutions(condoId);
      set({ boardResolutions, error: null });
    } catch (error: any) {
      setError(error.message || 'Failed to fetch board resolutions');
    } finally {
      setLoading(false);
    }
  },

  createBoardResolution: async (resData) => {
    const { setLoading, setError, fetchBoardResolutions } = get();
    setLoading(true);
    try {
      await apiService.createBoardResolution(resData);
      await fetchBoardResolutions(resData.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to create board resolution');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  updateBoardResolution: async (id, resData) => {
    const { setLoading, setError, boardResolutions, fetchBoardResolutions } = get();
    setLoading(true);
    try {
      await apiService.updateBoardResolution(id, resData);
      const existing = boardResolutions.find(r => r.id === id);
      await fetchBoardResolutions(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to update board resolution');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  closeBoardResolution: async (id) => {
    const { setLoading, setError, boardResolutions, fetchBoardResolutions } = get();
    setLoading(true);
    try {
      const existing = boardResolutions.find(r => r.id === id);
      await apiService.closeBoardResolution(id);
      await fetchBoardResolutions(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to close board resolution');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  deleteBoardResolution: async (id) => {
    const { setLoading, setError, boardResolutions, fetchBoardResolutions } = get();
    setLoading(true);
    try {
      const existing = boardResolutions.find(r => r.id === id);
      await apiService.deleteBoardResolution(id);
      await fetchBoardResolutions(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to delete board resolution');
      throw error;
    } finally {
      setLoading(false);
    }
  },
});
